<template>
  <ValidationObserver
    ref="observer"
  >
    <v-card>
      <v-toolbar
        dense
        :color="isCalculatedAnalysis() ? '#7EB0BF' : 'primary'"
        dark
      >
        <v-toolbar-title
          class="blocTitle"
        >
          <p>
            <slot name="title" />
          </p>
        </v-toolbar-title>
        <v-spacer />
      </v-toolbar>

      <v-container
        class="content-card"
      >
        <div class="d-flex align-center justify-end mb-4 mt-4">
          <div
            class="flex-grow-1"
            style="position: relative"
          >
            <VTextField
              :id="analysis.id || analysis._id"
              v-model="analysis.measure.value"
              dense
              outlined
              :disabled="!hasPermission('analyses.edit') || isCompletedOrLocked || isCalculatedAnalysis()"
              veeValidateInteractionMode="eager"
              formatNumber
              :label="unitSuffix"
              :hint="limits"
              :cssVars="cssVars"
              persistentHint
              :extraClass="`measure ${color(analysis.measure.value, analysis.norm)}`"
              :rules="{decimal: 8, required: false, min: 0, max: 200, analysisCondition0To14: conditionError}"
              @blur="submitData"
              @keyup.enter="submitData"
            />
            <div
              style="position: absolute; top: 1%; right: 1%;"
            >
              <span
                class="measure-dot"
                style="width: 12.5px !important; height: 12.5px !important"
                :class="`${color(analysis.measure.value, analysis.norm)}`"
              />
            </div>
          </div>
        </div>
        <div class="mb-4">
          <v-btn
            outlined
            large
            class="add-comment_btn pl-1"
            :disabled="!analysis.measure.value || isCompletedOrLocked"
            @click="updateDatas()"
          >
            {{ analysis.measure.note || $t('forms.labels.comment') }}
          </v-btn>
        </div>
        <v-progress-linear
          v-if="loading"
          indeterminate
        />
        <div v-else>
          <div v-if="measureHistory.length">
            <div>
              {{ $t('services.visits.lastResults') }} :
            </div>
            <div class="measure-history-list mb-4">
              <div
                v-for="(measure, i) in measureHistory"
                :key="`${measure.measuredAt}-${i}`"
              >
                <span
                  class="dot"
                  :class="measure.color"
                />
                {{ displayedMeasureHistory(measure) }}
                <v-icon v-if="measure.note">
                  mdi-message
                </v-icon>
              </div>
            </div>
            <router-link :to="measureHistoryRoute">
              {{ $t('services.visits.showAll') }}
            </router-link>
          </div>
          <v-container v-else>
            <v-row
              align="center"
              justify="space-between"
            >
              <h5>
                {{ $t('msg.noMeasureHistory') }}
              </h5>
              <v-btn
                v-show="isCalculatedAnalysis()"
                fab
                xSmall
                color="secondary"
                @click="loadData(true)"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-row>
          </v-container>
        </div>
      </v-container>
    </v-card>
  </ValidationObserver>
</template>

<script>
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import { cloneDeep, isPlainObject } from 'lodash';
import { ValidationObserver } from 'vee-validate';
import normsMixin from '@/mixins/NormsMixin';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import Vue from 'vue';
import AddAutomaticSentenceFormVue from '@/components/sites/visitSummaryComponents/AddAutomaticSentenceForm.vue';

const maxDiffInSbetweenUploadOfSameMeasureValue = 5;

export default {
    components: {
        ValidationObserver,
        VTextField,
    },
    mixins: [
        normsMixin,
    ],
    provide() {
        return {
            $validator: this.$validator,
        };
    },
    props: {
        populateWith: {
            type: Object,
            default: () => ({ empty: true }),
        },
        site: {
            type: Object,
            required: true,
        },
        componentId: {
            type: String,
            default: null,
        },
        activeView: {
            type: String,
            default: 'gridView',
        },
        onClickRemoveAnalysis: {
            type: Function,
            default: () => null,
        },
        isCompletedOrLocked: {
            type: Boolean,
            default: false,
        },
        analysisMeasureHistory: {
            type: Array,
            default: () => ([]),
        },
    },
    data() {
        return {
            normsClass: null,
            measureHistory: [],
            loading: false,
            analysis: {
                active: null,
                id: '',
                component: this.componentId,
                analysisType: {
                    ERPCode: '',
                    active: null,
                    id: '',
                    key: '',
                    name: {},
                    measureUnit: {},
                    type: '',
                },
                measure: {
                    name: '',
                    description: '',
                    unit: '',
                    value: undefined,
                    note: undefined,
                    category: undefined,
                    sentence: undefined,
                    measuredDate: null,
                },
                norm: {
                    veryLow: '',
                    low: '',
                    minimum: '',
                    maximum: '',
                    high: '',
                    veryHigh: '',
                },
            },
            conditionError: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasMeasure',
            'hasPermission',
            'measures',
            'siteLang',
        ]),
        measureHistoryRoute() {
            return {
                name: 'history',
                params: {
                    clientId: this.clientId,
                    // eslint-disable-next-line no-underscore-dangle
                    measuresAndSamplesCollectActivity: this.analysis.activity.id || this.analysis.activity._id,
                    analysisTypeId: this.analysis.analysisType.ref,
                    componentId: this.analysis.component,
                    siteId: this.site.ref,
                },
            };
        },
        displayedMeasureHistory() {
            return measure => `${moment(measure.measuredAt)
                .format('L LT')}: ${measure.value}`;
        },
        limits() {
            const {
                minimum,
                maximum,
            } = this.analysis.norm;

            if (minimum && maximum) {
                return this.$t('services.visits.limits', [minimum, maximum]);
            }
            if (minimum && !maximum) {
                return this.$t('services.visits.limitsMin', minimum);
            }
            if (!minimum && maximum) {
                return this.$t('services.visits.limitsMax', maximum);
            }

            return this.$t('services.visits.noLimits');
        },
        clientId() {
            return this.site.client.ref;
        },
        unitSuffix() {
            const { measureUnit } = this.analysis.analysisType;
            let unit = this.$t('services.visits.noUnit');

            if (isPlainObject(measureUnit)) {
                unit = measureUnit[this.siteLang];
            }

            return unit;
        },
        cssVars() {
            return {
                '--border-color': this.convertClassToColors(this.color(this.analysis.measure.value, this.analysis.norm)),
            };
        },
    },
    watch: {
        populateWith: {
            handler() {
                this.loadData();
            },
            deep: true,
        },
    },
    created() {
        this.loadData();
        this.getMeasureHistory();
    },
    methods: {
        ...mapActions({
            reloadWorkOrder: 'getWorkOrderInfo',
        }),
        async loadData(forceRelod = false) {
            if (forceRelod) {
                const { data } = await this.$rq.getAnalysisMeasure(
                    this.populateWith.activity.workOrder,
                    // eslint-disable-next-line no-underscore-dangle
                    this.populateWith.activity.id || this.populateWith.activity._id,
                    // eslint-disable-next-line no-underscore-dangle
                    this.populateWith.id || this.populateWith._id,
                );

                if (data.measure) {
                    this.analysis = data;
                    this.populateWith.measure = this.analysis.measure;
                }
            }
            if (!this.populateWith.empty) {
                this.analysis = Object.assign(this.analysis, cloneDeep(this.populateWith));
                if (this.analysis.measure.value) {
                    this.changedMeasuredValue(this.analysis.measure.value);
                }
            }
            // if norm is not populated
            if (!this.analysis.norm.ref) {
                this.setLimits(this.analysis.norm);
            }
        },
        async getMeasureHistory() {
            const measures = this.analysisMeasureHistory;
            if (measures?.length) {
                this.measureHistory = measures.map(measure => {
                    const {
                        low,
                        veryLow,
                        minimum,
                        maximum,
                        high,
                        veryHigh,
                    } = measure.analysis.norm;
                    return {
                        measuredAt: new Date(measure.measuredAt),
                        value: measure.value,
                        veryLow,
                        low,
                        minimum,
                        maximum,
                        high,
                        veryHigh,
                        color: this.color(measure.value, measure.analysis.norm),
                    };
                });
            }
        },
        changedMeasuredValue(value) {
            this.normsClass = this.changeColor(value);
        },
        async submitData() {
            const now = new Date();
            // To avoid uploading two time the same value to quickly (for instance after 'Enter' key press and then a blur even on the input)
            if (this.measureHistory.length && this.measureHistory[0].value === this.analysis.measure.value) {
                const diffInS = (now - this.measureHistory[0].measuredAt) / 1000;
                if (diffInS < maxDiffInSbetweenUploadOfSameMeasureValue) return;
            }
            const isValid = await this.$refs.observer.validate();
            if (isValid) {
                const bodyParams = {
                    value: this.analysis.measure.value || null,
                    note: this.analysis.measure.note,
                    measuredAt: now.toISOString(),
                    // eslint-disable-next-line no-underscore-dangle
                    id: this.analysis.id || this.analysis._id,
                };
                const workOrderId = this.analysis.activity.workOrder;
                const { data } = await this.$rq.updateWorkOrderActivityAnalysisMeasure(
                    workOrderId,
                    // eslint-disable-next-line no-underscore-dangle
                    this.analysis.activity.id || this.analysis.activity._id,
                    [bodyParams],
                );
                this.conditionError = !!data?.conditionMatchError;
                this.populateWith.measure = this.analysis.measure;
                this.loadData(true);
                this.reloadWorkOrder(this.populateWith.activity.workOrder);
            }
        },
        isCalculatedAnalysis() {
            return this.populateWith.type === 'CalculatedAnalysis' || this.populateWith.type === 'CalculatedAnalysisType';
        },
        updateDatas() {
            this.$root.$emit('showModal', {
                title: this.$t('actions.add.note'),
                component: Vue.component('AddAutomaticSentenceFormVue', AddAutomaticSentenceFormVue),
                payload: {
                    measure: this.analysis.measure,
                    isAnalysis: true,
                },
                onConfirm: requestPayload => {
                    if (requestPayload !== null) {
                        this.updateMeasureNote(requestPayload);
                    }
                },
            });
        },
        async updateMeasureNote(requestPayload) {
            await this.getTables();
            await this.$rq.updateMeasureNote(
                // eslint-disable-next-line no-underscore-dangle
                this.populateWith.activity.id || this.populateWith.activity._id,
                this.tableId,
                // eslint-disable-next-line no-underscore-dangle
                this.populateWith.measure.id || this.populateWith.measure._id,
                requestPayload,
            );
            this.analysis.measure.note = requestPayload.note;
            this.analysis.measure.category = requestPayload.category || undefined;
            this.analysis.measure.sentence = requestPayload.sentence || undefined;
            this.loadData(true);
            this.showMessage();
        },
        showMessage() {
            this.$toasted.global.toast_success({
                message: this.$t('msg.tableUpdated'),
            });
            this.$emit('reloadTable');
        },
        async getTables() {
            // eslint-disable-next-line no-underscore-dangle
            const { data } = await this.$rq.getTables(this.populateWith.activity.id || this.populateWith.activity._id);
            this.tableId = data.tables[0].id;
        },
    },
};
</script>

<style lang="scss" scoped>
@import "src/assets/styles/utils/variables.scss";

.remove-analysis-btn {
    position: absolute;
    bottom: 12px;
    right: 12px;
}

.content-card {
    min-height: 286px;
}

.add-comment_btn {
    text-transform: none;
    max-width: 100% !important;
    min-width: 100% !important;
    overflow: hidden;
    white-space: nowrap;
    justify-content: start;
    letter-spacing: normal;
}

@media screen and (max-width: 600px) {
    .blocTitle {
        p {
            font-size: 1rem !important;
            overflow: hidden;
            vertical-align: center;
            text-overflow: ellipsis !important;
            margin-bottom: 0
        }
    }
}

::v-deep .measure.normsRed {
    .v-messages__message {
        color: $norms-red;
    }
}
</style>
